import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../services/auth";
import {
  Mail,
  LockKeyhole,
  ShieldCheck,
  Eye,
  EyeOff,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  KeyRound,
} from "lucide-react";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function strengthScore(pw: string) {
  let s = 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

const pickMessage = (err: any, fallback: string) =>
  err?.response?.data?.message ||
  err?.response?.data?.Message ||
  err?.response?.data?.error ||
  fallback;

function InputLabel(props: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3">
      <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/46">
        {props.children}
      </p>
      {props.right}
    </div>
  );
}

function FieldShell(props: {
  icon: React.ReactNode;
  children: React.ReactNode;
  error?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[18px] border bg-white/76 backdrop-blur-sm transition",
        props.error
          ? "border-red-500/35 focus-within:border-red-500/55"
          : "border-brand-brown/10 hover:border-brand-brown/20 focus-within:border-brand-brown/28",
      )}
    >
      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-brown/38">
        {props.icon}
      </div>

      <div className={cn(props.trailing ? "pr-12" : "")}>{props.children}</div>

      {props.trailing && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {props.trailing}
        </div>
      )}
    </div>
  );
}

type ActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary";
};

function ActionButton({
  variant = "primary",
  className,
  children,
  ...props
}: ActionButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex h-11 w-full items-center justify-center gap-2 rounded-[18px] px-4 transition",
        "text-[10px] font-medium uppercase tracking-[0.18em]",
        variant === "primary"
          ? "bg-brand-brown text-brand-ivory shadow-[0_12px_24px_rgba(43,31,26,0.14)] hover:opacity-95 disabled:bg-brand-brown/20 disabled:text-brand-ivory/70"
          : "border border-brand-brown/10 bg-white/80 text-brand-brown/70 hover:border-brand-brown/22 hover:bg-brand-brown/5 hover:text-brand-brown disabled:border-brand-brown/10 disabled:text-brand-brown/30",
        "disabled:cursor-not-allowed",
        className,
      )}
    >
      {children}
    </button>
  );
}

export default function RegisterForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isVN = i18n.language?.startsWith("vi");

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState<"register" | "otp">("register");

  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<{
    username?: string;
    email?: string;
    password?: string;
    confirm?: string;
    agree?: string;
    otp?: string;
    form?: string;
  }>({});

  const score = useMemo(() => strengthScore(password), [password]);
  const strengthPct = Math.round((score / 4) * 100);

  const strengthLabel = useMemo(() => {
    if (score <= 1) return t("register.strength.weak");
    if (score === 2) return t("register.strength.fair");
    if (score === 3) return t("register.strength.good");
    return t("register.strength.strong");
  }, [score, t]);

  const inputBase =
    "h-12 w-full bg-transparent px-4 text-[15px] text-brand-brown outline-none placeholder:text-brand-brown/28";
  const inputWithIcon = "pl-11";

  const canSubmitRegister = useMemo(() => {
    return (
      username.trim().length > 0 &&
      email.trim().length > 0 &&
      password.length >= 6 &&
      confirm.length > 0 &&
      confirm === password &&
      agree &&
      !loading
    );
  }, [username, email, password, confirm, agree, loading]);

  const canSubmitOtp = useMemo(() => {
    const v = otp.trim();
    return v.length >= 4 && v.length <= 10 && !loading;
  }, [otp, loading]);

  const validateRegister = () => {
    const e: typeof errors = {};
    const em = email.trim();
    const un = username.trim();

    if (!un) e.username = t("register.errors.usernameRequired");
    else if (un.length < 3)
      e.username = t("register.errors.usernameMin", { n: 3 });

    if (!em) e.email = t("register.errors.emailRequired");
    else if (!/^\S+@\S+\.\S+$/.test(em))
      e.email = t("register.errors.emailInvalid");

    if (!password) e.password = t("register.errors.passwordRequired");
    else if (password.length < 6)
      e.password = t("register.errors.passwordMin", { n: 6 });

    if (!confirm) e.confirm = t("register.errors.confirmRequired");
    else if (confirm !== password)
      e.confirm = t("register.errors.passwordMismatch");

    if (!agree) e.agree = t("register.errors.agreeRequired");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateOtp = () => {
    const e: typeof errors = {};
    const v = otp.trim();

    if (!v) e.otp = t("register.errors.otpRequired");

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmitRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));
    if (!validateRegister()) return;

    try {
      setLoading(true);

      await authApi.register({
        username: username.trim(),
        email: email.trim().toLowerCase(),
        password,
        roleId: 2,
      });

      setStage("otp");
    } catch (err: any) {
      const apiMessage = pickMessage(err, t("register.errors.registerFailed"));
      const lower = String(apiMessage).toLowerCase();

      if (lower.includes("email")) {
        setErrors((m) => ({ ...m, email: apiMessage }));
      } else if (lower.includes("tên đăng nhập") || lower.includes("username")) {
        setErrors((m) => ({ ...m, username: apiMessage }));
      } else {
        setErrors((m) => ({ ...m, form: apiMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors((prev) => ({ ...prev, form: undefined }));
    if (!validateOtp()) return;

    try {
      setLoading(true);
      await authApi.confirmEmail(otp.trim());
      navigate("/auth?tab=login", { replace: true });
    } catch (err: any) {
      const apiMessage = pickMessage(err, t("register.errors.otpVerifyFailed"));
      setErrors((m) => ({ ...m, otp: apiMessage }));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleRegister = () => {
    setErrors((prev) => ({
      ...prev,
      form: t("register.errors.googleRegisterNotAvailable"),
    }));
  };

  if (stage === "otp") {
    return (
      <form onSubmit={onSubmitOtp} className="space-y-4">
        <div>
          <InputLabel>{t("register.otp.title")}</InputLabel>
          <p className="mb-3 text-[13px] leading-5 text-brand-brown/56">
            {isVN ? "Nhập mã gửi tới" : "Enter the code sent to"}{" "}
            <span className="font-medium text-brand-brown">{email.trim()}</span>
          </p>

          <FieldShell
            icon={<KeyRound className="h-4 w-4" />}
            error={!!errors.otp}
          >
            <input
              inputMode="numeric"
              placeholder={t("register.otp.placeholder")}
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                if (errors.otp) setErrors((m) => ({ ...m, otp: undefined }));
                if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
              }}
              className={cn(inputBase, inputWithIcon)}
            />
          </FieldShell>

          {errors.otp && (
            <p className="mt-1.5 text-[12px] text-red-600/80">{errors.otp}</p>
          )}
        </div>

        {errors.form && (
          <div className="rounded-[18px] border border-red-500/20 bg-red-500/5 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600/80" />
              <p className="text-[13px] leading-5 text-red-600/80">{errors.form}</p>
            </div>
          </div>
        )}

        <div className="grid gap-2.5">
          <ActionButton type="submit" variant="primary" disabled={!canSubmitOtp}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("register.otp.verifying")}
              </>
            ) : (
              t("register.otp.verify")
            )}
          </ActionButton>

          <ActionButton
            type="button"
            variant="secondary"
            disabled={loading}
            onClick={() => {
              setStage("register");
              setOtp("");
              setErrors({});
            }}
          >
            {t("register.otp.backToRegister")}
          </ActionButton>
        </div>

        <div className="rounded-[18px] border border-brand-brown/10 bg-white/46 px-4 py-3 text-center text-[13px] text-brand-brown/56">
          <Link
            to="/auth?tab=login"
            className="font-medium text-brand-brown transition hover:text-brand-gold"
          >
            {t("register.otp.alreadyConfirmed")}
          </Link>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmitRegister} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <InputLabel>{t("register.usernameLabel")}</InputLabel>
          <FieldShell
            icon={<ShieldCheck className="h-4 w-4" />}
            error={!!errors.username}
          >
            <input
              type="text"
              placeholder={t("register.usernamePlaceholder")}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (errors.username)
                  setErrors((m) => ({ ...m, username: undefined }));
                if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
              }}
              autoComplete="username"
              className={cn(inputBase, inputWithIcon)}
            />
          </FieldShell>
          {errors.username && (
            <p className="mt-1.5 text-[12px] text-red-600/80">{errors.username}</p>
          )}
        </div>

        <div>
          <InputLabel>{t("register.emailLabel")}</InputLabel>
          <FieldShell
            icon={<Mail className="h-4 w-4" />}
            error={!!errors.email}
          >
            <input
              type="email"
              inputMode="email"
              placeholder={t("register.emailPlaceholder")}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((m) => ({ ...m, email: undefined }));
                if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
              }}
              autoComplete="email"
              className={cn(inputBase, inputWithIcon)}
            />
          </FieldShell>
          {errors.email && (
            <p className="mt-1.5 text-[12px] text-red-600/80">{errors.email}</p>
          )}
        </div>

        <div>
          <InputLabel
            right={
              password.length > 0 ? (
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/40">
                  {strengthLabel}
                </span>
              ) : undefined
            }
          >
            {t("register.passwordLabel")}
          </InputLabel>

          <FieldShell
            icon={<LockKeyhole className="h-4 w-4" />}
            error={!!errors.password}
            trailing={
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="grid h-8 w-8 place-items-center rounded-[12px] border border-brand-brown/10 transition hover:border-brand-brown/20 hover:bg-brand-brown/5"
                aria-label={
                  showPw
                    ? t("register.hidePassword")
                    : t("register.showPassword")
                }
              >
                {showPw ? (
                  <EyeOff className="h-4 w-4 text-brand-brown/55" />
                ) : (
                  <Eye className="h-4 w-4 text-brand-brown/55" />
                )}
              </button>
            }
          >
            <input
              type={showPw ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password)
                  setErrors((m) => ({ ...m, password: undefined }));
                if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
              }}
              autoComplete="new-password"
              className={cn(inputBase, inputWithIcon, "pr-12")}
            />
          </FieldShell>

          <div className="mt-2 h-[5px] overflow-hidden rounded-full bg-brand-brown/8">
            <div
              className="h-full rounded-full bg-brand-gold transition-all"
              style={{ width: `${strengthPct}%` }}
            />
          </div>

          {errors.password && (
            <p className="mt-1.5 text-[12px] text-red-600/80">{errors.password}</p>
          )}
        </div>

        <div>
          <InputLabel>{t("register.confirmLabel")}</InputLabel>
          <FieldShell
            icon={<ShieldCheck className="h-4 w-4" />}
            error={!!errors.confirm}
            trailing={
              <button
                type="button"
                onClick={() => setShowCf((v) => !v)}
                className="grid h-8 w-8 place-items-center rounded-[12px] border border-brand-brown/10 transition hover:border-brand-brown/20 hover:bg-brand-brown/5"
                aria-label={
                  showCf
                    ? t("register.hideConfirmPassword")
                    : t("register.showConfirmPassword")
                }
              >
                {showCf ? (
                  <EyeOff className="h-4 w-4 text-brand-brown/55" />
                ) : (
                  <Eye className="h-4 w-4 text-brand-brown/55" />
                )}
              </button>
            }
          >
            <input
              type={showCf ? "text" : "password"}
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (errors.confirm)
                  setErrors((m) => ({ ...m, confirm: undefined }));
                if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
              }}
              autoComplete="new-password"
              className={cn(inputBase, inputWithIcon, "pr-12")}
            />
          </FieldShell>

          {confirm.length > 0 && !errors.confirm && confirm === password && (
            <div className="mt-1.5 flex items-center gap-2 text-[12px] text-brand-brown/52">
              <CheckCircle2 className="h-4 w-4 text-brand-gold" />
              {t("register.passwordsMatch")}
            </div>
          )}

          {errors.confirm && (
            <p className="mt-1.5 text-[12px] text-red-600/80">{errors.confirm}</p>
          )}
        </div>
      </div>

      <div className="rounded-[18px] border border-brand-brown/10 bg-white/54 px-4 py-3">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => {
              setAgree(e.target.checked);
              if (errors.agree) setErrors((m) => ({ ...m, agree: undefined }));
              if (errors.form) setErrors((m) => ({ ...m, form: undefined }));
            }}
            className="mt-0.5 h-4 w-4 rounded border-brand-brown/20"
          />

          <span className="text-[13px] leading-5 text-brand-brown/56">
            {isVN ? "Tôi đồng ý với" : "I agree to"}{" "}
            <a
              href="#"
              className="underline underline-offset-4 hover:text-brand-brown transition"
              onClick={(e) => e.preventDefault()}
            >
              {t("register.terms")}
            </a>{" "}
            {t("register.and")}{" "}
            <a
              href="#"
              className="underline underline-offset-4 hover:text-brand-brown transition"
              onClick={(e) => e.preventDefault()}
            >
              {t("register.privacy")}
            </a>
            .
          </span>
        </label>

        {errors.agree && (
          <p className="mt-1.5 text-[12px] text-red-600/80">{errors.agree}</p>
        )}
      </div>

      {errors.form && (
        <div className="rounded-[18px] border border-red-500/20 bg-red-500/5 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600/80" />
            <p className="text-[13px] leading-5 text-red-600/80">{errors.form}</p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
        <ActionButton
          type="submit"
          variant="primary"
          disabled={!canSubmitRegister}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("register.creating")}
            </>
          ) : (
            t("register.createAccount")
          )}
        </ActionButton>

        <ActionButton
          type="button"
          variant="secondary"
          onClick={onGoogleRegister}
          disabled={loading}
        >
          Google
        </ActionButton>
      </div>

      <div className="rounded-[18px] border border-brand-brown/10 bg-white/46 px-4 py-3 text-center text-[13px] text-brand-brown/56">
        <Link
          to="/auth?tab=login"
          className="font-medium text-brand-brown transition hover:text-brand-gold"
        >
          {t("register.alreadyHaveAccount")}
        </Link>
      </div>
    </form>
  );
}